import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Phone, MapPin, Navigation, CheckCircle2, Clock, PlayCircle, MessageCircle } from "lucide-react";
import { customerWa } from "@/lib/customerMsg";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { toast } from "sonner";

const T = {
  en: { title: "Your CHELA job", customer: "Customer", call: "Call customer", task: "Task", when: "When", addr: "Address", map: "Open map & navigate", nomap: "No live location shared. Use the address or call the customer.", start: "I have reached", done: "Job done", confirm: "Mark this job as done?", started: "Started", finished: "Job completed. Thank you!", notfound: "This job link is not valid. Please contact CHELA.", rules: "Be polite. Never ask the customer for cash or their UPI PIN. Payment is already done online.", help: "Need help? WhatsApp CHELA", tell_reached: "Tell customer: I have reached", tell_done: "Tell customer: job done" },
  hi: { title: "आपका CHELA जॉब", customer: "ग्राहक", call: "ग्राहक को कॉल करें", task: "काम", when: "कब", addr: "पता", map: "मैप खोलें और रास्ता देखें", nomap: "लाइव लोकेशन नहीं है। पता देखें या ग्राहक को कॉल करें।", start: "मैं पहुँच गया", done: "काम पूरा हुआ", confirm: "क्या यह काम पूरा हो गया?", started: "शुरू हुआ", finished: "काम पूरा हुआ। धन्यवाद!", notfound: "यह जॉब लिंक सही नहीं है। CHELA से संपर्क करें।", rules: "विनम्र रहें। ग्राहक से कभी कैश या UPI PIN न माँगें। पेमेंट पहले ही ऑनलाइन हो चुका है।", help: "मदद चाहिए? CHELA को WhatsApp करें", tell_reached: "ग्राहक को बताएं: मैं पहुँच गया", tell_done: "ग्राहक को बताएं: काम पूरा" },
};

function Row({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3 py-3 border-b border-[#EFEAE3] last:border-0">
      <Icon size={20} className="text-[#E05624] shrink-0 mt-0.5" />
      <div><div className="text-xs font-bold uppercase text-[#8A8D95]">{label}</div><div className="text-[#121316] font-semibold">{children}</div></div>
    </div>
  );
}

export default function Job() {
  const { token } = useParams();
  const { lang, setLang } = useLang();
  const c = T[lang] || T.hi;
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/jobs/${token}`).then((r) => setJob({ ...r.data, job_token: token })).catch(() => setErr(true));
  }, [token]);

  const act = async (action) => {
    if (action === "done" && !window.confirm(c.confirm)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${token}/status`, { action });
      setJob((j) => ({ ...j, job_status: data.job_status }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };

  const head = (
    <div className="flex items-center justify-between">
      <div className="font-display font-extrabold text-2xl text-[#121316]">CHELA</div>
      <button data-testid="job-lang-toggle" onClick={() => setLang(lang === "hi" ? "en" : "hi")} className="h-9 px-4 rounded-full border border-[#E5E0D8] text-sm font-bold bg-white">{lang === "hi" ? "EN" : "हिंदी"}</button>
    </div>
  );

  if (err) return <div className="min-h-screen bg-[#FAF8F5] p-5 max-w-md mx-auto">{head}<p data-testid="job-not-found" className="mt-10 text-center text-[#5A5D65]">{c.notfound}</p></div>;
  if (!job) return <div className="min-h-screen bg-[#FAF8F5] p-5 max-w-md mx-auto">{head}</div>;

  const done = job.job_status === "done";
  const tell = (kind, testid, label) => (
    <a data-testid={testid} href={customerWa(kind, job)} target="_blank" rel="noreferrer" className="h-14 rounded-full btn-whatsapp font-bold inline-flex items-center justify-center gap-2"><MessageCircle size={18} /> {label}</a>
  );
  return (
    <div className="min-h-screen bg-[#FAF8F5] p-5 pb-10 max-w-md mx-auto">
      {head}
      <h1 className="mt-6 font-display font-extrabold text-3xl text-[#121316]">{c.title}</h1>
      <p className="text-sm text-[#5A5D65]">ID {job.ref}{job.job_status === "started" && ` · ${c.started}`}</p>

      <div data-testid="job-details" className="mt-5 rounded-3xl bg-white border border-[#E5E0D8] px-5">
        <Row icon={Phone} label={c.customer}>{job.name} · +91 {String(job.phone).replace(/\D/g, "").slice(-10)}</Row>
        <Row icon={PlayCircle} label={c.task}>{job.service}{job.description ? ` — ${job.description}` : ""}</Row>
        <Row icon={Clock} label={c.when}>{job.date} · {job.time} · {job.duration}</Row>
        <Row icon={MapPin} label={c.addr}>{job.location || "-"}</Row>
      </div>

      <div className="mt-4 grid gap-3">
        {job.maps_link
          ? <a data-testid="job-map" href={job.maps_link} target="_blank" rel="noreferrer" className="h-14 rounded-full bg-[#2457A6] text-white font-bold inline-flex items-center justify-center gap-2"><Navigation size={18} /> {c.map}</a>
          : <p data-testid="job-no-map" className="text-sm text-[#5A5D65] text-center">{c.nomap}</p>}
        <a data-testid="job-call" href={`tel:+91${String(job.phone).replace(/\D/g, "").slice(-10)}`} className="h-14 rounded-full btn-whatsapp font-bold inline-flex items-center justify-center gap-2"><Phone size={18} /> {c.call}</a>
      </div>

      <p className="mt-5 text-sm text-[#3A3D45] bg-[#FCF4E6] border border-[#F0E2C4] rounded-xl px-4 py-3">{c.rules}</p>

      <div className="mt-5 grid gap-3">
        {done ? (
          <>
            <div data-testid="job-done-msg" className="rounded-2xl bg-[#E8F7EE] text-[#1B8A4B] font-bold p-5 text-center inline-flex items-center justify-center gap-2"><CheckCircle2 size={22} /> {c.finished}</div>
            {tell("done", "job-tell-done", c.tell_done)}
          </>
        ) : (
          <>
            {job.job_status !== "started" && <button data-testid="job-start" onClick={() => act("start")} disabled={busy} className="h-14 rounded-full btn-dark font-bold disabled:opacity-60">{c.start}</button>}
            {job.job_status === "started" && tell("reached", "job-tell-reached", c.tell_reached)}
            <button data-testid="job-done" onClick={() => act("done")} disabled={busy} className="h-16 rounded-full btn-saffron font-extrabold text-lg inline-flex items-center justify-center gap-2 disabled:opacity-60"><CheckCircle2 size={22} /> {c.done}</button>
          </>
        )}
      </div>
    </div>
  );
}
